import { FilterValues, WeekRange } from '../types'
import { ensureString } from '@/utils/ensureString'

type ScalarField = {
  kind: 'scalar'
  filterKey: 'status' | 'customer' | 'order' | 'workPackage' | 'filterByDate'
  paramKey: string
}

type RangeField = {
  kind: 'range'
  startKey: string
  endKey: string
}

type TeamField = {
  kind: 'team'
  leaderKey: 'leader'
  memberKey: 'member'
}

type BooleanField = {
  kind: 'boolean'
  filterKey: 'billable' | 'unbilled' | 'showWithRevenue'
  paramKey: string
}

type Field = ScalarField | RangeField | TeamField | BooleanField

const FIELDS: readonly Field[] = [
  { kind: 'scalar', filterKey: 'status', paramKey: 'status' },
  { kind: 'scalar', filterKey: 'customer', paramKey: 'customer' },
  { kind: 'scalar', filterKey: 'order', paramKey: 'order' },
  { kind: 'scalar', filterKey: 'workPackage', paramKey: 'workPackage' },
  { kind: 'scalar', filterKey: 'filterByDate', paramKey: 'filterByDate' },
  { kind: 'range', startKey: 'dateStart', endKey: 'dateEnd' },
  { kind: 'team', leaderKey: 'leader', memberKey: 'member' },
  { kind: 'boolean', filterKey: 'billable', paramKey: 'billable' },
  { kind: 'boolean', filterKey: 'unbilled', paramKey: 'unbilled' },
  {
    kind: 'boolean',
    filterKey: 'showWithRevenue',
    paramKey: 'showWithRevenue',
  },
] as const

export const FILTER_PARAM_KEYS = FIELDS.flatMap((f) => {
  if (f.kind === 'scalar') return [f.paramKey]
  if (f.kind === 'range') return [f.startKey, f.endKey]
  if (f.kind === 'team') return [f.leaderKey, f.memberKey]
  return [f.paramKey]
})

export type FilterParams = Record<string, string>

const readScalar = (
  field: ScalarField,
  params: Partial<FilterParams>,
  out: FilterValues,
) => {
  const v = params[field.paramKey]
  if (v) (out[field.filterKey] as string) = v
}

const readRange = (
  field: RangeField,
  params: Partial<FilterParams>,
  out: FilterValues,
) => {
  const start = params[field.startKey]
  const end = params[field.endKey]
  if (start && end) out.dateRange = { startDate: start, endDate: end }
}

const readTeam = (
  field: TeamField,
  params: Partial<FilterParams>,
  out: FilterValues,
) => {
  const leader = params[field.leaderKey]
  const member = params[field.memberKey]
  if (!leader && !member) return
  if (member) {
    out.team = member
    out.teamLeaderId = leader || undefined
    out.isTeamLeader = false
    return
  }
  if (leader) {
    out.team = leader
    out.isTeamLeader = true
  }
}

const readBoolean = (
  field: BooleanField,
  params: Partial<FilterParams>,
  out: FilterValues,
) => {
  const v = params[field.paramKey]
  if (v === 'true') out[field.filterKey] = true
  else if (v === 'false') out[field.filterKey] = false
}

export const readFiltersFromParams = (
  params: Partial<FilterParams>,
): FilterValues => {
  const out: FilterValues = {
    status: null,
    team: null,
    customer: null,
    order: null,
    workPackage: null,
  }
  FIELDS.forEach((f) => {
    if (f.kind === 'scalar') readScalar(f, params, out)
    else if (f.kind === 'range') readRange(f, params, out)
    else if (f.kind === 'team') readTeam(f, params, out)
    else readBoolean(f, params, out)
  })
  return out
}

const writeScalar = (field: ScalarField, filters: FilterValues) => ({
  [field.paramKey]: ensureString(filters[field.filterKey]),
})

const writeRange = (field: RangeField, range?: WeekRange) => ({
  [field.startKey]: ensureString(range?.startDate),
  [field.endKey]: ensureString(range?.endDate),
})

const writeTeam = (field: TeamField, filters: FilterValues) => {
  const leader = filters.isTeamLeader ? filters.team : filters.teamLeaderId
  const member = filters.isTeamLeader ? null : filters.team
  return {
    [field.leaderKey]: ensureString(leader),
    [field.memberKey]: ensureString(member),
  }
}

const writeBoolean = (field: BooleanField, filters: FilterValues) => {
  const value = filters[field.filterKey]
  return {
    [field.paramKey]: typeof value === 'boolean' ? String(value) : '',
  }
}

export const filtersToParams = (filters: FilterValues): FilterParams =>
  FIELDS.reduce<FilterParams>((acc, f) => {
    if (f.kind === 'scalar') return { ...acc, ...writeScalar(f, filters) }
    if (f.kind === 'range') {
      return { ...acc, ...writeRange(f, filters.dateRange) }
    }
    if (f.kind === 'team') return { ...acc, ...writeTeam(f, filters) }
    return { ...acc, ...writeBoolean(f, filters) }
  }, {})
